interface BaseTaskItem {
  id: number;
  path_step?: {
    week_order?: number;
    order_within_week?: number | null;
    task?: {
      name: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
  week_order?: number;
  order_within_week?: number | null;
  task?: {
    name: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Gets the week order from an item, handling different data structures
 */
function getWeekOrder(item: BaseTaskItem): number {
  return item.path_step?.week_order ?? item.week_order ?? Number.MAX_SAFE_INTEGER
}

/**
 * Gets the order within week from an item, handling different data structures
 */
function getOrderWithinWeek(item: BaseTaskItem): number | null | undefined {
  return item.path_step?.order_within_week ?? item.order_within_week
}

/**
 * Sorts curriculum items by week_order and order_within_week
 * Items without order come first, then items with order sorted numerically
 * @param items Array of curriculum items to sort
 * @returns Sorted array of items
 */
export function sortCurriculumItems<T extends BaseTaskItem>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    // First sort by week_order
    const aWeekOrder = getWeekOrder(a)
    const bWeekOrder = getWeekOrder(b)
    if (aWeekOrder !== bWeekOrder) {
      return aWeekOrder - bWeekOrder
    }
    
    // Then sort by order_within_week, with null/undefined values coming first
    const aOrder = getOrderWithinWeek(a)
    const bOrder = getOrderWithinWeek(b)
    
    // If both are null/undefined, maintain original order
    if ((aOrder === null || aOrder === undefined) && (bOrder === null || bOrder === undefined)) {
      return 0
    }
    
    // If only one has order_within_week, prioritize the one without value
    if (aOrder === null || aOrder === undefined) return -1
    if (bOrder === null || bOrder === undefined) return 1
    
    // If both have order_within_week, compare them numerically
    return aOrder - bOrder
  })
}

/**
 * Groups curriculum items by week and sorts items within each week
 * @param items Array of curriculum items to process
 * @returns Object with weeks as keys and sorted arrays of items as values
 */
export function groupAndSortByWeek<T extends BaseTaskItem>(items: T[]): Record<string, T[]> {
  const byWeek: Record<string, T[]> = {}
  
  // First sort all items
  const sortedItems = sortCurriculumItems(items)
  
  // Group items by week
  sortedItems.forEach(item => {
    const weekOrder = getWeekOrder(item)
    if (!byWeek[weekOrder]) {
      byWeek[weekOrder] = []
    }
    byWeek[weekOrder].push(item)
  })
  
  return byWeek
} 